'use client';

import { useMemo, useState } from 'react';
import {
  TO_BE_DEFINED_LABEL,
  type KnockoutNode,
  type Match,
  type Team,
} from '@/types/tournament.model';
import { updateMatchDateTime } from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';
import { toast } from '@/components/ui/use-toast';
import { MatchDateTimeEditModal } from '../match/MatchDateTimeEditModal';
import { ForcedReasonBadge } from '../match/ForcedReasonBadge';

interface BracketViewProps {
  roundsByLevel: Record<number, KnockoutNode[]>;
  maxRound: number;
  roundNames: Record<number, string>;
  getTeamName: (teamId: string | Team) => string;
  tournamentId?: string;
  onResultUpdated?: () => void;
}

const COL_WIDTH = 240;
const CARD_WIDTH = 200;
// 140 fits the tallest card: header row (~25px) + ForcedReasonBadge row (~33px,
// only rendered when node.match.forcedReason is set) + the two team rows
// (~68px), measured against the real Tailwind classes — see task-6-report.md.
const CARD_HEIGHT = 140;
const MIN_SLOT_HEIGHT = 130;
const HEADER_OFFSET_Y = 50;

const formatMatchTime = (
  startDate: string | null | undefined,
): string | null => {
  if (!startDate) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const SHORT_DATE_FMT = new Intl.DateTimeFormat('es', {
  day: 'numeric',
  month: 'short',
});

const formatMatchDate = (
  startDate: string | null | undefined,
): string | null => {
  if (!startDate) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  if (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  ) {
    return 'Hoy';
  }
  return SHORT_DATE_FMT.format(d).replace(/\.$/, '');
};

/**
 * Layout del bracket:
 *  - La primera ronda reparte sus N partidos en bandas verticales de
 *    `slotHeight` (mínimo MIN_SLOT_HEIGHT). Cada card mide CARD_HEIGHT
 *    fijo (BYE incluido) → no hay solapes.
 *  - Cada ronda siguiente coloca su card r,i en el midpoint de las dos
 *    cards hijas (r-1, 2i) y (r-1, 2i+1). De esta forma la card queda
 *    siempre visualmente centrada entre sus feeders, sin importar la
 *    cantidad de rondas.
 *  - Las líneas conectoras se dibujan desde el lado derecho de cada
 *    card hacia el centro vertical de su parent (estilo bracket clásico).
 */
export function BracketView({
  roundsByLevel,
  maxRound,
  roundNames,
  getTeamName,
  tournamentId,
  onResultUpdated,
}: BracketViewProps) {
  const round1Count = roundsByLevel[1]?.length ?? 0;
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [editingTeamNames, setEditingTeamNames] = useState<{
    team1: string;
    team2: string;
  }>({ team1: '', team2: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canEdit = !!tournamentId;

  const openEditModal = (
    match: Match,
    team1Name: string,
    team2Name: string,
  ) => {
    if (!canEdit) return;
    setEditingTeamNames({ team1: team1Name, team2: team2Name });
    setEditingMatch(match);
  };

  const handleSaveDateTime = async (startTime: string) => {
    if (!editingMatch || !tournamentId) return;
    try {
      setIsSubmitting(true);
      await updateMatchDateTime(tournamentId, editingMatch.id, startTime);
      onResultUpdated?.();
      toast({
        title: 'Fecha y horario actualizados',
        description:
          'La fecha y horario del partido han sido actualizados correctamente.',
        variant: 'default',
      });
      setEditingMatch(null);
    } catch (error) {
      console.error('Error al actualizar fecha y horario:', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo actualizar la fecha y horario. Inténtalo de nuevo.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { positions, totalHeight } = useMemo(() => {
    const pos: Record<number, Record<number, number>> = {};
    if (round1Count === 0) {
      return { positions: pos, totalHeight: 200 };
    }

    // MIN_SLOT_HEIGHT is a floor for small cards; at the current CARD_HEIGHT
    // this Math.max always resolves via CARD_HEIGHT + 30 (170 > 130). It only
    // matters again if CARD_HEIGHT drops back under ~101.
    const slotHeight = Math.max(MIN_SLOT_HEIGHT, CARD_HEIGHT + 30);
    const round1Height = round1Count * slotHeight;

    pos[1] = {};
    for (let i = 0; i < round1Count; i += 1) {
      pos[1][i] = HEADER_OFFSET_Y + (i + 0.5) * slotHeight;
    }

    for (let r = 2; r <= maxRound; r += 1) {
      pos[r] = {};
      const nodes = roundsByLevel[r] ?? [];
      const prevPos = pos[r - 1] ?? {};
      for (let i = 0; i < nodes.length; i += 1) {
        const left = prevPos[2 * i];
        const right = prevPos[2 * i + 1];
        if (left !== undefined && right !== undefined) {
          pos[r][i] = (left + right) / 2;
        } else if (left !== undefined) {
          pos[r][i] = left;
        } else if (right !== undefined) {
          pos[r][i] = right;
        } else {
          pos[r][i] = HEADER_OFFSET_Y + round1Height / 2;
        }
      }
    }

    return {
      positions: pos,
      totalHeight: HEADER_OFFSET_Y + round1Height + 30,
    };
  }, [maxRound, roundsByLevel, round1Count]);

  const totalWidth = maxRound * COL_WIDTH;

  return (
    <div className="overflow-x-auto">
      {editingMatch && (
        <MatchDateTimeEditModal
          match={editingMatch}
          team1Name={editingTeamNames.team1}
          team2Name={editingTeamNames.team2}
          open={!!editingMatch}
          onOpenChange={(open) => {
            if (!open) setEditingMatch(null);
          }}
          onSave={handleSaveDateTime}
          isSubmitting={isSubmitting}
        />
      )}
      <svg width={totalWidth} height={totalHeight} className="bracket-svg">
        {/* Headers de ronda */}
        {Array.from({ length: maxRound }, (_, i) => i + 1).map((round) => (
          <text
            key={`round-${round}`}
            x={(round - 1) * COL_WIDTH + COL_WIDTH / 2}
            y={28}
            textAnchor="middle"
            className="fill-slate-700 text-sm font-medium"
          >
            {roundNames[round] || `Ronda ${round}`}
          </text>
        ))}

        {/* Connectors: para cada card en ronda r (r < maxRound), dibujar
            L-line desde el lado derecho hacia el centro vertical de su parent. */}
        {Array.from({ length: maxRound - 1 }, (_, i) => i + 1).map((round) => {
          const nodes = roundsByLevel[round] ?? [];
          return nodes.map((_, index) => {
            const childY = positions[round]?.[index];
            const parentY = positions[round + 1]?.[Math.floor(index / 2)];
            if (childY === undefined || parentY === undefined) return null;
            const startX =
              (round - 1) * COL_WIDTH + (COL_WIDTH + CARD_WIDTH) / 2;
            const midX = round * COL_WIDTH;
            const endX = round * COL_WIDTH + (COL_WIDTH - CARD_WIDTH) / 2;
            return (
              <path
                key={`conn-${round}-${index}`}
                d={`M ${startX} ${childY} H ${midX} V ${parentY} H ${endX}`}
                stroke="#CBD5E1"
                strokeWidth={2}
                fill="none"
              />
            );
          });
        })}

        {/* Cards */}
        {Object.entries(roundsByLevel).map(([roundStr, nodes]) => {
          const round = Number.parseInt(roundStr);
          return nodes.map((node, index) => {
            const y = positions[round]?.[index];
            if (y === undefined) return null;
            const x = (round - 1) * COL_WIDTH + (COL_WIDTH - CARD_WIDTH) / 2;
            const top = y - CARD_HEIGHT / 2;

            if (node.advancingTeam && !node.match) {
              return (
                <foreignObject
                  key={`bye-${round}-${index}`}
                  x={x}
                  y={top}
                  width={CARD_WIDTH}
                  height={CARD_HEIGHT}
                >
                  <div className="flex h-full w-full items-center justify-center rounded border-2 border-dashed border-blue-300 bg-blue-50 p-2 text-center">
                    <div>
                      <div className="text-sm font-medium text-blue-700">
                        {getTeamName(node.advancingTeam)}
                      </div>
                      <div className="mt-1 text-xs text-blue-500">
                        (Pasa directo)
                      </div>
                    </div>
                  </div>
                </foreignObject>
              );
            }

            if (!node.match) return null;

            const team1Name = node.match.team1
              ? getTeamName(node.match.team1)
              : TO_BE_DEFINED_LABEL;
            const team2Name = node.match.team2
              ? getTeamName(node.match.team2)
              : TO_BE_DEFINED_LABEL;
            let winner: 1 | 2 | null = null;
            if (node.match.result?.isValid) {
              winner =
                node.match.result.team1Score > node.match.result.team2Score
                  ? 1
                  : 2;
            }
            const score1 =
              node.match.result?.team1Score !== undefined
                ? node.match.result.team1Score
                : '-';
            const score2 =
              node.match.result?.team2Score !== undefined
                ? node.match.result.team2Score
                : '-';

            const timeLabel = formatMatchTime(node.match.startDate);
            const dateLabel = formatMatchDate(node.match.startDate);

            return (
              <foreignObject
                key={`node-${round}-${index}`}
                x={x}
                y={top}
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
              >
                <div className="flex h-full w-full flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
                  {(timeLabel || canEdit) &&
                    (canEdit ? (
                      <button
                        type="button"
                        onClick={() =>
                          openEditModal(node.match!, team1Name, team2Name)
                        }
                        className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-2 py-1 text-left transition-colors hover:bg-slate-100"
                        title="Editar fecha y horario"
                      >
                        <span className="font-mono text-xs font-semibold tabular-nums text-slate-700">
                          {timeLabel ?? 'Sin horario'}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                          {dateLabel ?? 'Editar'}
                        </span>
                      </button>
                    ) : (
                      timeLabel && (
                        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-2 py-1">
                          <span className="font-mono text-xs font-semibold tabular-nums text-slate-700">
                            {timeLabel}
                          </span>
                          {dateLabel && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              {dateLabel}
                            </span>
                          )}
                        </div>
                      )
                    ))}
                  {node.match.forcedReason && (
                    <div className="border-b border-slate-100 px-2 py-1">
                      <ForcedReasonBadge
                        reason={node.match.forcedReason}
                        matchId={node.match.id}
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col justify-center p-2">
                    <div
                      className={`mb-1 flex items-center justify-between rounded-sm p-1 ${
                        winner === 1 ? 'bg-green-50' : ''
                      }`}
                    >
                      <span className="truncate text-xs font-medium">
                        {team1Name}
                      </span>
                      <span className="text-xs font-semibold">{score1}</span>
                    </div>
                    <div
                      className={`flex items-center justify-between rounded-sm p-1 ${
                        winner === 2 ? 'bg-green-50' : ''
                      }`}
                    >
                      <span className="truncate text-xs font-medium">
                        {team2Name}
                      </span>
                      <span className="text-xs font-semibold">{score2}</span>
                    </div>
                  </div>
                </div>
              </foreignObject>
            );
          });
        })}
      </svg>
    </div>
  );
}
